import { useCallback, useEffect, useMemo, useState } from 'react';
import { getBackendUrl } from '../lib/apiUrls';
import {
  fromDateInputValue,
  fromDateTimeLocalValue,
} from '../lib/dateTime';
import {
  buildRecurrencePayload,
  createRecurrenceForm,
} from '../lib/recurrence';
import { createWorkspaceClient } from '../lib/workspaceClient';

const initialEventForm = {
  title: '',
  description: '',
  location: '',
  startAt: '',
  endAt: '',
  allDay: false,
  tags: '',
  recurrence: createRecurrenceForm(),
};

function trimString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeOptionalString(value) {
  const trimmed = trimString(value);
  return trimmed || null;
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) {
    return tags.map((tag) => trimString(tag)).filter(Boolean);
  }

  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
}

function buildCreateEventPayload(event) {
  return {
    title: trimString(event?.title),
    description: normalizeOptionalString(event?.description),
    location: normalizeOptionalString(event?.location),
    startAt: fromDateTimeLocalValue(event?.startAt),
    endAt: normalizeOptionalString(fromDateTimeLocalValue(event?.endAt)),
    allDay: Boolean(event?.allDay),
    tags: normalizeTags(event?.tags),
    recurrence: buildRecurrencePayload(event?.recurrence, {
      includeCompletionState: false,
    }),
  };
}

function buildUpdateEventPayload(updates) {
  const payload = { ...updates };

  if (Object.prototype.hasOwnProperty.call(payload, 'title')) {
    payload.title = trimString(payload.title);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'description')) {
    payload.description = normalizeOptionalString(payload.description);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'location')) {
    payload.location = normalizeOptionalString(payload.location);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'startAt')) {
    payload.startAt = fromDateTimeLocalValue(payload.startAt);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'endAt')) {
    payload.endAt = normalizeOptionalString(fromDateTimeLocalValue(payload.endAt));
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'tags')) {
    payload.tags = normalizeTags(payload.tags);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'recurrence')) {
    payload.recurrence = buildRecurrencePayload(payload.recurrence, {
      includeCompletionState: false,
    });
  }

  return payload;
}

function getUpdateSuccessMessage(updates) {
  switch (updates?.status) {
    case 'completed':
      return 'Event marked completed.';
    case 'cancelled':
      return 'Event cancelled.';
    default:
      return 'Event updated successfully.';
  }
}

function normalizeEvent(event) {
  return {
    ...event,
    id: event.id || event.eventId || event._id,
    scope: event.scope || (event.scopeType === 'family' ? 'family' : 'personal'),
  };
}

export function useCalendar(currentUser) {
  const workspaceClient = useMemo(() => createWorkspaceClient(getBackendUrl()), []);
  const [calendarData, setCalendarData] = useState([]);
  const [routineTasks, setRoutineTasks] = useState([]);
  const [calendarParams, setCalendarParams] = useState({ status: 'all', startFrom: '', startTo: '' });
  const [calendarError, setCalendarError] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState({
    type: 'idle',
    message: 'No calendar events available.',
  });
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);
  const [eventForm, setEventForm] = useState(initialEventForm);

  const resolveEventScope = useCallback(
    (eventId, fallback = 'personal') =>
      calendarData.find((event) => event.id === eventId)?.scope || fallback,
    [calendarData]
  );

  const loadCalendar = useCallback(async (options = {}) => {
    if (!currentUser?.userId) {
      setCalendarData([]);
      setRoutineTasks([]);
      setCalendarStatus({
        type: 'idle',
        message: 'Sign in to view calendar events.',
      });
      return;
    }

    setCalendarError(null);
    setCalendarStatus({ type: 'loading', message: 'Loading calendar events...' });
    setIsLoadingCalendar(true);

    try {
      const [response, tasksResponse] = await Promise.all([
        workspaceClient.getEvents(currentUser.userId, {
          ...calendarParams,
          startFrom: fromDateInputValue(calendarParams.startFrom, 'start'),
          startTo: fromDateInputValue(calendarParams.startTo, 'end'),
        }),
        workspaceClient.getTasks(currentUser.userId, { status: 'all' }).catch(() => ({ tasks: [] })),
      ]);
      const fetchedEvents = Array.isArray(response?.events) ? response.events : response;
      const fetchedTasks = Array.isArray(tasksResponse?.tasks) ? tasksResponse.tasks : tasksResponse;
      setCalendarData((Array.isArray(fetchedEvents) ? fetchedEvents : []).map(normalizeEvent));
      setRoutineTasks((Array.isArray(fetchedTasks) ? fetchedTasks : []).map((task) => ({
        ...task,
        id: task.id || task.taskId || task._id,
        scope: task.scope || (task.scopeType === 'family' ? 'family' : 'personal'),
      })));
      setCalendarStatus({
        type: 'success',
        message:
          options.successMessage ||
          (fetchedEvents.length ? 'Calendar loaded.' : 'No calendar events available.'),
      });
    } catch (error) {
      const message = error?.error || error?.message || 'Could not load calendar events.';
      setCalendarError(message);
      setCalendarStatus({
        type: 'error',
        message,
      });
    } finally {
      setIsLoadingCalendar(false);
    }
  }, [calendarParams, currentUser?.userId, workspaceClient]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  const refreshCalendar = useCallback(async () => {
    await loadCalendar();
  }, [loadCalendar]);

  const completeRoutineTask = useCallback(
    async (taskId) => {
      if (!currentUser?.userId || !taskId) return false;
      setCalendarError(null);
      setIsLoadingCalendar(true);
      try {
        await workspaceClient.completeTask(currentUser.userId, taskId, { scope: 'family' });
        await loadCalendar({ successMessage: 'Routine completed for today.' });
        return true;
      } catch (error) {
        const message = error?.error || error?.message || 'Could not complete routine.';
        setCalendarError(message);
        setCalendarStatus({ type: 'error', message });
        return false;
      } finally {
        setIsLoadingCalendar(false);
      }
    },
    [currentUser?.userId, loadCalendar, workspaceClient]
  );

  const onEventFormChange = useCallback((field, value) => {
    setEventForm((current) => ({ ...current, [field]: value }));
  }, []);

  const onCalendarFilterChange = useCallback((field, value) => {
    setCalendarParams((current) => ({ ...current, [field]: value }));
  }, []);

  const onCalendarFiltersChange = useCallback((updates) => {
    setCalendarParams((current) => ({ ...current, ...updates }));
  }, []);

  const onCreateEvent = useCallback(
    async (event, options = {}) => {
      if (!currentUser?.userId) return false;
      const eventPayload = buildCreateEventPayload(event);
      const scope = event?.scope === 'family' ? 'family' : 'personal';

      if (scope === 'family' && options.canUseFamilyScope === false) {
        const message = 'Create or join a family before adding family events.';
        setCalendarError(message);
        setCalendarStatus({ type: 'error', message });
        return false;
      }

      if (!eventPayload.title) {
        const message = 'Event title is required.';
        setCalendarError(message);
        setCalendarStatus({ type: 'error', message });
        return false;
      }

      if (!eventPayload.startAt) {
        const message = 'Event start time is required.';
        setCalendarError(message);
        setCalendarStatus({ type: 'error', message });
        return false;
      }

      setCalendarError(null);
      setCalendarStatus({ type: 'loading', message: 'Creating event...' });
      setIsLoadingCalendar(true);
      try {
        await workspaceClient.createEvent(currentUser.userId, { ...eventPayload, scope });
        setEventForm(initialEventForm);
        await loadCalendar({ successMessage: 'Event created successfully.' });
        return true;
      } catch (error) {
        const message = error?.error || error?.message || 'Could not create calendar event.';
        setCalendarError(message);
        setCalendarStatus({ type: 'error', message });
        return false;
      } finally {
        setIsLoadingCalendar(false);
      }
    },
    [currentUser?.userId, loadCalendar, workspaceClient]
  );

  const onUpdateEvent = useCallback(
    async (eventId, updates) => {
      if (!currentUser?.userId) return false;
      setCalendarError(null);
      setCalendarStatus({ type: 'loading', message: 'Updating event...' });
      setIsLoadingCalendar(true);
      try {
        await workspaceClient.updateEvent(
          currentUser.userId,
          eventId,
          buildUpdateEventPayload(updates),
          { scope: resolveEventScope(eventId) }
        );
        await loadCalendar({ successMessage: getUpdateSuccessMessage(updates) });
        return true;
      } catch (error) {
        const message = error?.error || error?.message || 'Could not update calendar event.';
        setCalendarError(message);
        setCalendarStatus({ type: 'error', message });
        return false;
      } finally {
        setIsLoadingCalendar(false);
      }
    },
    [currentUser?.userId, loadCalendar, resolveEventScope, workspaceClient]
  );

  const onDeleteEvent = useCallback(
    async (eventId) => {
      if (!currentUser?.userId) return false;
      setCalendarError(null);
      setCalendarStatus({ type: 'loading', message: 'Deleting event...' });
      setIsLoadingCalendar(true);
      try {
        await workspaceClient.deleteEvent(currentUser.userId, eventId, {
          scope: resolveEventScope(eventId),
        });
        await loadCalendar({ successMessage: 'Event deleted successfully.' });
        return true;
      } catch (error) {
        const message = error?.error || error?.message || 'Could not delete calendar event.';
        setCalendarError(message);
        setCalendarStatus({ type: 'error', message });
        return false;
      } finally {
        setIsLoadingCalendar(false);
      }
    },
    [currentUser?.userId, loadCalendar, resolveEventScope, workspaceClient]
  );

  const canShowEmptyState = useMemo(
    () => !isLoadingCalendar && !calendarData.length && !calendarError,
    [calendarData.length, calendarError, isLoadingCalendar]
  );

  return {
    calendarData,
    routineTasks,
    calendarParams,
    calendarError,
    calendarStatus,
    eventForm,
    isLoadingCalendar,
    canShowEmptyState,
    refreshCalendar,
    completeRoutineTask,
    onEventFormChange,
    onCalendarFilterChange,
    onCalendarFiltersChange,
    onCreateEvent,
    onUpdateEvent,
    onDeleteEvent,
    setCalendarParams,
  };
}
